import {createContext, useContext, type ReactNode} from 'react';
import {Link2} from 'lucide-react';
import {MIRROR_FIELDS, type MirrorFieldKey} from '@common/enrollmentSite/mirror';

export interface MirrorState {
  sourceName: string;
  sourceSlug: string;
  mirroredFields: ReadonlySet<string>;
}

// `sectionLocked`: the builder already locked the whole section, so fields inside don't
// repeat the lock.
const MirrorContext = createContext<{mirror: MirrorState | null; sectionLocked: boolean}>({
  mirror: null,
  sectionLocked: false,
});

export const MirrorProvider = MirrorContext.Provider;

export type SectionMirrorState = 'none' | 'partial' | 'full';

export function sectionMirrorState(
  sectionId: string,
  mirroredFields: ReadonlySet<string> | undefined,
): SectionMirrorState {
  const fields = MIRROR_FIELDS.filter((f) => f.section === sectionId);
  if (!mirroredFields || fields.length === 0) return 'none';
  const mirrored = fields.filter((f) => mirroredFields.has(f.key)).length;
  if (mirrored === 0) return 'none';
  return mirrored === fields.length ? 'full' : 'partial';
}

/** Disables its children (native fieldset) when `field` follows the source site. */
export function MirrorLock({field, children}: {field: MirrorFieldKey; children: ReactNode}) {
  const {mirror, sectionLocked} = useContext(MirrorContext);
  if (!mirror || sectionLocked || !mirror.mirroredFields.has(field)) return <>{children}</>;
  return (
    <fieldset disabled className='m-0 min-w-0 space-y-1.5 border-0 p-0'>
      <p className='flex items-center gap-1 text-xs text-muted-foreground'>
        <Link2 className='h-3 w-3' />
        Mirrored from {mirror.sourceName}
      </p>
      <div className='space-y-5 opacity-60'>{children}</div>
    </fieldset>
  );
}

/** For sections that lock their own controls instead of wrapping them in MirrorLock. */
export function useMirroredField(): {sourceName?: string; isMirrored: (key: string) => boolean} {
  const {mirror, sectionLocked} = useContext(MirrorContext);
  return {
    sourceName: mirror?.sourceName,
    isMirrored: (key) => !!mirror && (sectionLocked || mirror.mirroredFields.has(key)),
  };
}

